import React from 'react';
import {
  Box,
  Button,
  ButtonMode,
  Dialog,
  NotificationInfoIcon
} from 'edenred-ui';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import {
  SemiBold,
  ModalMessage
} from '@epi-pages/LoadBenefit/components/Topup/TopupForm/TopupConfirmationModal/TopupConfirmationModal.styles';
import { TopupConfirmationModalProps } from '@epi-models/pages/Transfer';
import { useFormContext } from 'react-hook-form';
import { FormFields } from '@epi-forms/helpers';

export const ConfirmationContent = ({
  handleTopupRequest,
  handleCloseModal,
  employeesCount,
  totalAmount,
  isTopupPending,
  showOnHoldText,
  isPendingBenefit,
  topupAmount
}: Omit<
  TopupConfirmationModalProps,
  | 'showModal'
  | 'showOnboardingContent'
  | 'topupRequestStatus'
  | 'resetTopupStatus'
>) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const benefitType = watch(FormFields.Benefit);
  const { sizeExtraExtraLarge, fontSemiBold, gray100 } = useTheme();

  return (
    <Box>
      <Dialog.Title>
        <Box display={'flex'} justifyContent={'flex-start'} gap={2}>
          <NotificationInfoIcon />
          <Box
            id="topup-confirmation_modal-title"
            fontSize={sizeExtraExtraLarge}
            fontWeight={fontSemiBold}
            color={gray100}
          >
            {t('containers.deposit_money.topup_tab.confirmation_modal.title')}
          </Box>
        </Box>
      </Dialog.Title>
      <Dialog.Content>
        <Box
          id="topup-confirmation_modal"
          display={'flex'}
          flexDirection={'column'}
          alignItems={'center'}
          my={-3}
        >
          <ModalMessage>
            {isPendingBenefit && (
              <Box id="topup-confirmation_modal-pending-benefit-message">
                <Trans
                  defaults={t(
                    'containers.deposit_money.topup_tab.confirmation_modal.pending_benefit_content',
                    {
                      benefitType: t(
                        `containers.deposit_money.topup_tab.confirmation_modal.benefits.${benefitType}`
                      )
                    }
                  )}
                  components={{
                    bold: <SemiBold />
                  }}
                />
              </Box>
            )}
            <div id="topup-confirmation_modal-message">
              <Trans
                defaults={t(
                  'containers.deposit_money.topup_tab.confirmation_modal.content',
                  {
                    benefitType: t(
                      `containers.deposit_money.topup_tab.confirmation_modal.benefits.${benefitType}`
                    ),
                    topupAmount,
                    employeesCount,
                    totalAmount
                  }
                )}
                components={{
                  bold: <SemiBold />
                }}
              />
            </div>
            {showOnHoldText && (
              <Box id="topup-confirmation_modal-onhold-message">
                <Trans
                  i18nKey={
                    'containers.deposit_money.topup_tab.confirmation_modal.on_hold_content'
                  }
                  components={{
                    bold: <SemiBold />
                  }}
                />
              </Box>
            )}
          </ModalMessage>
        </Box>
      </Dialog.Content>
      <Dialog.Actions>
        <Box
          display={'flex'}
          justifyContent={'space-between'}
          gap={2}
          width="100%"
          pl={6}
          pr={1}
        >
          <Button
            mode={ButtonMode.Link}
            onClick={handleCloseModal}
            id="topup-confirmation_cancel"
          >
            {t(
              'containers.deposit_money.topup_tab.confirmation_modal.buttons.cancel'
            )}
          </Button>
          <Button
            onClick={handleTopupRequest}
            isProcessing={isTopupPending}
            id="topup-confirmation_confirm"
          >
            {t(
              'containers.deposit_money.topup_tab.confirmation_modal.buttons.confirm'
            )}
          </Button>
        </Box>
      </Dialog.Actions>
    </Box>
  );
};
